import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import {
  TableView,
  AbstractTableViewsService,
} from '@tiltgroup/web-components';
import { Mutation, Query, UpsertTableViewGroupInput } from '@graphql-types';
import { map } from 'rxjs/operators';
import { ApolloQueryResult } from '@apollo/client/core';
import { QueriesBaseService } from '@services/queries/queries-base.service';
import {
  deleteTableViewMutation,
  setTableViewAsDefaultForGroupMutation,
  tableViewGroupQuery,
  unsetDefaultTableViewForGroupMutation,
  upsertTableViewGroupMutation,
} from '@services/queries/queries/table-views.queries';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TableViewsService
  extends QueriesBaseService
  implements AbstractTableViewsService
{
  private _apollo = inject(Apollo);

  getUserTableViews(
    name: string
  ): Observable<{ groupID: string; tableViews: TableView[] }> {
    return this._apollo
      .query({
        query: tableViewGroupQuery,
        variables: {
          name,
        },
      })
      .pipe(
        map((res: ApolloQueryResult<Query>) => res.data.tableViewGroup),
        map((tableViewGroup) => ({
          groupID: tableViewGroup?.id,
          tableViews: (tableViewGroup?.tableViews || []).map((tableView) => ({
            ...tableView,
            default: tableViewGroup.defaultTableView?.name === tableView?.name,
            ...(tableView?.sort && {
              sort: {
                active: tableView?.sort?.column,
                direction: tableView?.sort?.direction?.toLowerCase(),
              },
            }),
            ...(tableView?.queryParameters && {
              queryParameters: JSON.stringify(tableView?.queryParameters),
            }),
            pinnedFilters: tableView?.pinnedFilters,
          })) as TableView[],
        }))
      );
  }

  updateUserTableView(input: UpsertTableViewGroupInput) {
    return this._apollo
      .mutate({
        mutation: upsertTableViewGroupMutation,
        variables: {
          input,
        },
      })
      .pipe(
        map((res: ApolloQueryResult<Mutation>) => res.data.upsertTableViewGroup)
      );
  }

  deleteUserTableView(id: string) {
    return this._apollo
      .mutate({
        mutation: deleteTableViewMutation,
        variables: {
          id,
        },
      })
      .pipe(
        map((res: ApolloQueryResult<Mutation>) => res.data.deleteTableView)
      );
  }

  setTableViewAsDefault({
    isDefault,
    isSystem,
    groupId,
    groupName,
    tableViewName,
    tableViewId,
  }: {
    isDefault: boolean;
    isSystem: boolean;
    groupId: string;
    groupName: string;
    tableViewName?: string;
    tableViewId?: string;
  }) {
    if (isDefault) {
      return this._setTableViewAsDefault(
        groupName,
        isSystem,
        tableViewName,
        tableViewId
      );
    } else {
      return this._unsetDefaultTableView(groupId);
    }
  }

  private _setTableViewAsDefault(
    groupName: string,
    isSystem: boolean,
    tableViewName: string,
    tableViewId?: string
  ) {
    return this._apollo
      .mutate({
        mutation: setTableViewAsDefaultForGroupMutation,
        variables: {
          groupName,
          isSystem,
          tableViewName,
          tableViewId,
        },
      })
      .pipe(
        map(
          (res: ApolloQueryResult<Mutation>) =>
            res.data.setTableViewAsDefaultForGroup
        )
      );
  }

  private _unsetDefaultTableView(groupId: string) {
    return this._apollo
      .mutate({
        mutation: unsetDefaultTableViewForGroupMutation,
        variables: {
          groupId,
        },
      })
      .pipe(
        map(
          (res: ApolloQueryResult<Mutation>) =>
            res.data.unsetDefaultTableViewForGroup
        )
      );
  }
}
